
@switch (searchBarMode) {
  @case (searchBarModes.SIMPLE) {
    <ng-container *ngTemplateOutlet="searchBar"></ng-container>
  }
  @case (searchBarModes.ADVANCED) {
    <ng-container *ngTemplateOutlet="searchBar"></ng-container>
  }
  @case (searchBarModes.ALL_SEARCH_OPTIONS) {
    <ng-container *ngTemplateOutlet="searchBar"></ng-container>
  }
  @case (searchBarModes.FIELD) {
    <ng-container *ngTemplateOutlet="fieldSearch"></ng-container>
  }
}

<sh-display-settings-bar
  [displayMode]="displayMode$ | async"
  [displayOptions]="displayOptions"
  [sortInfo]="sortInfo$ | async"
  [sortOptions]="sortOptions$ | async"
  [showSortSelect]="allowSort"
  [showViewToggle]="showViewToggle"
  [sortPrefixMessage]="'sort.prefix' | translate"
  [sortLoadingMsg]="'general.filter.loading' | translate"
  [xxResultsLabelKey]="xxResultsLabelKey"
  [noResultsLabelKey]="noResultsLabelKey"
  [totalResults]="hasError ? 0 : (totalResults$ | async)"
  (sortInfoChanged)="onSortInfoChanged($event)"
  (displayModeChanged)="onViewModeSelected($event)"
  >
</sh-display-settings-bar>

@switch (displayMode$ | async) {
  @case (displayModes.LIST) {
    <ng-container *ngTemplateOutlet="listView"></ng-container>
  }
  @case (displayModes.GRID) {
    <ng-container *ngTemplateOutlet="gridView"></ng-container>
  }
  @case (displayModes.MAP) {
    <ng-container *ngTemplateOutlet="mapView"></ng-container>
  }
  @default {
    <ng-container *ngTemplateOutlet="listView"></ng-container>
  }
}

<ng-container *ngTemplateOutlet="pagination"></ng-container>

<ng-template #pagination>
  @if ((displayMode$ | async) !== displayModes.MAP) {
    <sh-pagination
      [paginationInfo]="paginationInfo$ | async"
      [previousPageMsg]="'components.pagination.previous' | translate"
      [nextPageMsg]="'components.pagination.next' | translate"
      [perPageMsg]="'components.pagination.per-page' | translate"
      [resultsPerPageOptions]="resultsPerPageOptions"
      [scrollRef]="scrollRef"
      (paginationInfoChanged)="onPaginationInfoChanged($event)"
    ></sh-pagination>
  }
</ng-template>

<ng-template #listView>
  <sh-generic-list
    [loadingMsg]="'ui.loading' | translate"
    [loadingFailedMsg]="hasError && errorMessage ? errorMessage : ('ui.loading-failed' | translate)"
    [emptyListMsg]="'ui.no-results-rights' | translate"
    [rowElements]="entries$ | async"
    [autoRefresh]="autoRefresh"
    [isLoading]="isLoading$ | async"
    [availableSelect]="availableSelect"
    [disableSelect]="usingBatch$ | async"
    [canBatchSelect]="canBatchSelect"
    [isInitialBulk]="!(usingBatch$ | async)"
    [disableSelectMessage]="disableSelectMessage | translate"
    [hasError]="hasError"
    [columns]="columns"
    [columnOptions]="columnOptions"
    [allowSort]="allowSort"
    [sortInfo]="sortInfo$ | async"
    [actionItems]="actionItems"
    [contextActionGroup]="contextActionGroup"
    [noItemsSelectedLabelKey]="noItemsSelectedLabelKey"
    [itemsSelectedLabelKey]="itemsSelectedLabelKey"
    [selectedElements]="selectedEntries$ | async"
    [selectedItemsCount]="selectedItemsCount | async"
    [activeItem]="activeItem"
    [highlightedItem]="highlightedItem"
    [areItemsClickable]="areItemsClickable"
    (selectionChanged)="handleSelectionChanged($event)"
    (sortInfoChanged)="onSortInfoChanged($event)"
    (itemClicked)="onItemClicked($event)"
    (itemSelected)="handleSelect($event)"
    (itemDeselected)="handleDeselect($event)"
    (cellClicked)="onCellClicked($event)"
    (columnSelectionChanged)="onColumnSelectionChanged($event)"
    >
  </sh-generic-list>
</ng-template>

<ng-template #gridView>
  <sh-generic-grid
    [gridType]="gridType"
    [loadingMsg]="'ui.loading' | translate"
    [loadingFailedMsg]="hasError && errorMessage ? errorMessage : ('ui.loading-failed' | translate)"
    [emptyListMsg]="'ui.no-results-rights' | translate"
    [gridElements]="entries$ | async"
    [isLoading]="isLoading$ | async"
    [availableSelect]="availableSelect"
    [disableSelect]="usingBatch$ | async"
    [canBatchSelect]="canBatchSelect"
    [isInitialBulk]="!(usingBatch$ | async)"
    [disableSelectMessage]="disableSelectMessage | translate"
    [hasError]="hasError"
    [actionItems]="actionItems"
    [contextActionGroup]="contextActionGroup"
    [noItemsSelectedLabelKey]="noItemsSelectedLabelKey"
    [itemsSelectedLabelKey]="itemsSelectedLabelKey"
    [selectedElements]="selectedEntries$ | async"
    [selectedItemsCount]="selectedItemsCount | async"
    [activeItem]="activeItem"
    [pictureFillMode]="pictureFillMode"
    [areItemsClickable]="areItemsClickable"
    (selectionChanged)="handleSelectionChanged($event)"
    (itemClicked)="onItemClicked($event)"
    (itemSelected)="handleSelect($event)"
    (itemDeselected)="handleDeselect($event)"
    (thumbnailClicked)="onThumbnailClicked($event)"
  ></sh-generic-grid>
</ng-template>

<ng-template #mapView>
  <sh-generic-map
    [loadingMsg]="'ui.loading' | translate"
    [loadingFailedMsg]="hasError && errorMessage ? errorMessage : ('ui.loading-failed' | translate)"
    [emptyListMsg]="'ui.no-results-rights' | translate"
    [mapElements]="entries$ | async"
    [isLoading]="isLoading$ | async"
    [hasError]="hasError"
    [actionItems]="actionItems"
  ></sh-generic-map>
</ng-template>

<ng-template #searchBar>
  @if (searchBarMode !== searchBarModes.NONE) {
    <sh-search-bar
      [enableAdvancedSearch]="searchBarMode === searchBarModes.ADVANCED || searchBarMode === searchBarModes.ALL_SEARCH_OPTIONS"
      [enableSemanticSearch]="searchBarMode === searchBarModes.ALL_SEARCH_OPTIONS"
      [enableFullTextSearch]="searchBarMode === searchBarModes.ALL_SEARCH_OPTIONS"
      [actionItems]="searchBarActionItems"
    ></sh-search-bar>
  }
</ng-template>

<ng-template #fieldSearch>
  <sh-field-search
    [fieldSearchOptions]="fieldSearchOptions"
    [placeholder]="'general.search-here' | translate"
    [prefix]="'general.field' | translate"
  ></sh-field-search>
</ng-template>
